import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  InputAdornment, IconButton, Dialog, DialogContent, DialogTitle, DialogActions, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import Alerts from '../../components/Alerts';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { QRCodeCanvas } from 'qrcode.react';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import html2canvas from 'html2canvas';
import { useRef } from 'react';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: theme.shadows[6],
  },
}));

const ClickableTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const LocationMarker = ({ setLocation, index }) => {
  const [position, setPosition] = useState(null);

  useMapEvents({
    click(e) {
      const newPosition = { lat: e.latlng.lat, lng: e.latlng.lng };
      setPosition(newPosition);
      setLocation(index, newPosition);
    },
  });

  return position === null ? null : <Marker position={position} />;
};

const AddUnit = ({ darkMode, companyCode, companyName }) => {
  const [view, setView] = useState('viewUnits');
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [error, setError] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    numUnits: 1,
    unitsData: [{ unitName: '', unitType: '', isPureVeg: '', city: '', address: '', gstin: '', pan: '', location: { lat: 0, lng: 0 }, companyName: companyName || '' }],
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const auth = getAuth();
  const [openUpiDialog, setOpenUpiDialog] = useState(false);
const [selectedUnitForUpi, setSelectedUnitForUpi] = useState(null);
const [upiData, setUpiData] = useState([]); // table
const [upiForm, setUpiForm] = useState({
  description: '',
  payeeName: '',
  upiAddress: ''
});
const [upiErrors, setUpiErrors] = useState({});
const qrRef = useRef();

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'units'));
        const unitsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).filter(unit => unit.companyCode === companyCode);
        setUnits(unitsData);
        applyFilters(unitsData, searchQuery, filterType);
      } catch (err) {
        console.error('Error fetching units:', err);
        setError('Failed to load unit data. Check Firestore permissions or data.');
      }
    };
    fetchUnits();
  }, [companyCode]);

  useEffect(() => {
    const newUnitsData = Array.from({ length: formData.numUnits }, (_, i) => ({
      unitName: formData.unitsData[i]?.unitName || '',
      unitType: formData.unitsData[i]?.unitType || '',
      isPureVeg: formData.unitsData[i]?.isPureVeg || '',
      city: formData.unitsData[i]?.city || '',
      address: formData.unitsData[i]?.address || '',
      gstin: formData.unitsData[i]?.gstin || '',
      pan: formData.unitsData[i]?.pan || '',
      location: formData.unitsData[i]?.location || { lat: 0, lng: 0 },
      companyName: companyName || formData.unitsData[i]?.companyName || '',
    }));
    setFormData((prev) => ({ ...prev, unitsData: newUnitsData }));
  }, [formData.numUnits, companyName]);

  const applyFilters = (items, query, type) => {
    let filtered = items;
    if (query) {
      filtered = filtered.filter((item) =>
        item.unitName.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (type !== 'All') {
      filtered = filtered.filter((item) => item.unitType === type);
    }
    setFilteredUnits(filtered);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(units, query, filterType);
  };

  const handleFilterTypeChange = (e) => {
    const type = e.target.value;
    setFilterType(type);
    applyFilters(units, searchQuery, type);
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    let newValue = value;

  // Auto uppercase GSTIN
    if (name === "gstin") {
      newValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    }

    if (name === "pan") {
      newValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    }
    /*setFormData((prev) => {
      const newUnitsData = [...prev.unitsData];
      newUnitsData[index] = { ...newUnitsData[index], [name]: value };
      return { ...prev, unitsData: newUnitsData };
    });*/
    setFormData((prev) => {
    const newUnitsData = [...prev.unitsData];
    newUnitsData[index] = { ...newUnitsData[index], [name]: newValue };
    return { ...prev, unitsData: newUnitsData };
  });
  };

  const setLocation = (index, location) => {
    setFormData((prev) => {
      const newUnitsData = [...prev.unitsData];
      newUnitsData[index] = { ...newUnitsData[index], location: location || { lat: 0, lng: 0 } };
      return { ...prev, unitsData: newUnitsData };
    });
  };

  const handleNumUnitsChange = (e) => {
    const value = Math.max(1, Math.min(10, Number(e.target.value)));
    setFormData((prev) => ({ ...prev, numUnits: value }));
  };

  const generateUnitCode = (companyCode, unitNumber, city) => {
    const cityCode = city ? city.slice(0, 2).toLowerCase() : 'xx';
    return `${companyCode}u${unitNumber}${cityCode}`;
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    const errors = {};
    formData.unitsData.forEach((unit, index) => {
      if (!unit.unitName || /[\\/]/.test(unit.unitName)) {
        errors[`unitName_${index}`] = 'Unit name cannot be empty or contain "/" or "\\".';
      }
      if (!unit.unitType) {
        errors[`unitType_${index}`] = 'Please select a unit type.';
      }
      if (!unit.isPureVeg) {
        errors[`isPureVeg_${index}`] = 'Please specify if the unit is Pure Veg or Non-Veg.';
      }
      if (!unit.city) {
        errors[`city_${index}`] = 'City cannot be empty.';
      }
      if (!unit.address) {
        errors[`address_${index}`] = 'Address cannot be empty.';
      }
      /*if (!unit.gstin) {
        errors[`gstin_${index}`] = 'GSTIN is required.';
      } else if (!gstinRegex.test(unit.gstin)) {
        errors[`gstin_${index}`] = 'Invalid GSTIN format.';
      }*/
      // GSTIN (optional)
      if (unit.gstin && !gstinRegex.test(unit.gstin)) {
        errors[`gstin_${index}`] = 'Invalid GSTIN format.';
      }
      // PAN (optional)
      if (unit.pan && !panRegex.test(unit.pan)) {
        errors[`pan_${index}`] = 'Invalid PAN format.';
      }
      /*if (!unit.pan) {
        errors[`pan_${index}`] = 'PAN is required.';
      } else if (!panRegex.test(unit.pan)) {
        errors[`pan_${index}`] = 'Invalid PAN format.';
      }*/
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const addPromises = formData.unitsData.map(async (unit, index) => {
        const unitNumber = index + 1;
        const unitCode = generateUnitCode(companyCode, unitNumber, unit.city);
        const unitDocRef = doc(db, 'units', unitCode);
        const unitDoc = await getDoc(unitDocRef);
        if (unitDoc.exists()) {
          throw new Error(`Unit code ${unitCode} already exists.`);
        }
        await setDoc(unitDocRef, {
          unitName: unit.unitName,
          unitType: unit.unitType,
          isPureVeg: unit.isPureVeg === 'Pure Veg',
          city: unit.city,
          address: unit.address,
          gstin: unit.gstin,
          pan: unit.pan,
          location: unit.location,
          companyCode: companyCode,
          companyName: unit.companyName,
          unitCode: unitCode,
          createdAt: new Date().toISOString(),
        });
      });

      await Promise.all(addPromises);

      setSnackbar({
        open: true,
        message: 'Units added successfully!',
        severity: 'success',
      });
      resetForm();
      setOpenAddDialog(false);
      const querySnapshot = await getDocs(collection(db, 'units'));
      const updatedUnits = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(unit => unit.companyCode === companyCode);
      setUnits(updatedUnits);
      applyFilters(updatedUnits, searchQuery, filterType);
    } catch (err) {
      console.error('Error adding units:', err);
      setFormErrors({ general: err.message || 'Failed to add units.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUnit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    const errors = {};
    const unit = formData.unitsData[0];
    if (!unit.unitName || /[\\/]/.test(unit.unitName)) {
      errors.unitName = 'Unit name cannot be empty or contain "/" or "\\".';
    }
    if (!unit.unitType) {
      errors.unitType = 'Please select a unit type.';
    }
    if (!unit.isPureVeg) {
      errors.isPureVeg = 'Please specify if the unit is Pure Veg or Non-Veg.';
    }
    if (!unit.city) {
      errors.city = 'City cannot be empty.';
    }
    if (!unit.address) {
      errors.address = 'Address cannot be empty.';
    }
    /*if (!unit.gstin) {
      errors.gstin = 'GSTIN is required.';
    } else if (!gstinRegex.test(unit.gstin)) {
      errors.gstin = 'Invalid GSTIN format.';
    }
    if (!unit.pan) {
      errors.pan = 'PAN is required.';
    } else if (!panRegex.test(unit.pan)) {
      errors.pan = 'Invalid PAN format.';
    }*/
    // GSTIN (optional)
    if (unit.gstin && !gstinRegex.test(unit.gstin)) {
      errors.gstin = 'Invalid GSTIN format.';
    }

    // PAN (optional)
    if (unit.pan && !panRegex.test(unit.pan)) {
      errors.pan = 'Invalid PAN format.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const unitCode = generateUnitCode(companyCode, 1, unit.city);
      const unitDocRef = doc(db, 'units', selectedUnit.id);
      await setDoc(unitDocRef, {
        unitName: unit.unitName,
        unitType: unit.unitType,
        isPureVeg: unit.isPureVeg === 'Pure Veg',
        city: unit.city,
        address: unit.address,
        gstin: unit.gstin,
        pan: unit.pan,
        location: unit.location,
        companyCode: companyCode,
        companyName: unit.companyName,
        unitCode: unitCode,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      setSnackbar({
        open: true,
        message: 'Unit updated successfully!',
        severity: 'success',
      });
      resetForm();
      setOpenEditDialog(false);
      setSelectedUnit(null);
      const querySnapshot = await getDocs(collection(db, 'units'));
      const updatedUnits = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(unit => unit.companyCode === companyCode);
      setUnits(updatedUnits);
      applyFilters(updatedUnits, searchQuery, filterType);
    } catch (err) {
      console.error('Error updating unit:', err);
      setFormErrors({ general: err.message || 'Failed to update unit.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUnit = async () => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'units', itemToDelete.id));
      setSnackbar({
        open: true,
        message: 'Unit deleted successfully!',
        severity: 'success',
      });
      const querySnapshot = await getDocs(collection(db, 'units'));
      const updatedUnits = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(unit => unit.companyCode === companyCode);
      setUnits(updatedUnits);
      applyFilters(updatedUnits, searchQuery, filterType);
      setOpenDeleteDialog(false);
      setItemToDelete(null);
      if (view === 'unitDetails') {
        setView('viewUnits');
        setSelectedUnit(null);
      }
    } catch (err) {
      console.error('Error deleting unit:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete unit.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const resetForm = () => {
    setFormData({
      numUnits: 1,
      unitsData: [{ unitName: '', unitType: '', isPureVeg: '', city: '', address: '', gstin: '', pan: '', location: { lat: 0, lng: 0 }, companyName: companyName || '' }],
    });
    setFormErrors({});
    setShowPassword(false);
  };

  const openEditForm = (unit) => {
    setSelectedUnit(unit);
    setFormData({
      numUnits: 1,
      unitsData: [{
        unitName: unit.unitName,
        unitType: unit.unitType,
        isPureVeg: unit.isPureVeg ? 'Pure Veg' : 'Non-Veg',
        city: unit.city || '',
        address: unit.address,
        gstin: unit.gstin || '',
        pan: unit.pan || '',
        location: unit.location || { lat: 0, lng: 0 },
        companyName: unit.companyName || companyName || '',
      }],
    });
    setOpenEditDialog(true);
  };

  const renderContent = () => {
    if (view === 'viewUnits') {
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Units List
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                resetForm();
                setOpenAddDialog(true);
              }}
              sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
            >
              Add Unit
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={handleFilterTypeChange}
                label="Type"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="foodcourt">Food Court</MenuItem>
                <MenuItem value="hotel">Hotel</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Search by Name"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ flexGrow: 1, maxWidth: 300, borderRadius: '8px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {filteredUnits.length > 0 ? (
            <StyledCard>
              <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Unit Name</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>City</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Pure Veg</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Company</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Address</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUnits.map((unit) => (
                      <ClickableTableRow
                        key={unit.id}
                        onClick={() => {
                          setSelectedUnit(unit);
                          setView('unitDetails');
                        }}
                      >
                        <TableCell sx={{ color: 'text.primary' }}>{unit.unitName}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{unit.unitType}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{unit.city}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{unit.isPureVeg ? 'Yes' : 'No'}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{unit.companyName}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{unit.address}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              onClick={() => openEditForm(unit)}
                              sx={{ color: 'primary.main' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setItemToDelete(unit);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete />
                            </IconButton>
                            <IconButton
  onClick={async () => {
    try {
      const unitDocRef = doc(db, 'units', unit.id);
      const unitDocSnap = await getDoc(unitDocRef);
      const unitData = unitDocSnap.data();
      //setSelectedUnitForUpi(unitData);
      setSelectedUnitForUpi({ id: unitDocSnap.id, ...unitData });
      //setUpiData(unitData.upiDetails || [{ description: '', payeeName: '', upiAddress: '' }]);
      setUpiData(unitData.upiDetails?.length ? unitData.upiDetails : [{ description: '', payeeName: '', upiAddress: '' }]);
      setOpenUpiDialog(true);
    } catch (err) {
      console.error('Failed to load UPI details:', err);
      setSnackbar({ open: true, message: 'Failed to load UPI details', severity: 'error' });
    }
  }}
  sx={{ color: 'secondary.main' }}
>
  <QrCodeIcon />
</IconButton>
                          </Box>
                        </TableCell>
                      </ClickableTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          ) : (
            <Typography color="text.secondary">
              No units available. Add a unit or adjust filters.
            </Typography>
          )}
        </Box>
      );
    }

    if (view === 'unitDetails' && selectedUnit) {
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={() => setView('viewUnits')}
              sx={{ mr: 2, color: 'text.primary' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Unit Details: {selectedUnit.unitName}
            </Typography>
          </Box>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2, color: 'text.primary' }}>
                Unit Information
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Type:</strong> {selectedUnit.unitType}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {selectedUnit.unitName}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>City:</strong> {selectedUnit.city}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Pure Veg:</strong> {selectedUnit.isPureVeg ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Company:</strong> {selectedUnit.companyName}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Address:</strong> {selectedUnit.address}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>GSTIN:</strong> {selectedUnit.gstin}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>PAN:</strong> {selectedUnit.pan}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Location:</strong> {`${selectedUnit.location.lat.toFixed(4)}, ${selectedUnit.location.lng.toFixed(4)}`}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Created At:</strong> {new Date(selectedUnit.createdAt).toLocaleString()}
              </Typography>
              {selectedUnit.updatedAt && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Updated At:</strong> {new Date(selectedUnit.updatedAt).toLocaleString()}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => openEditForm(selectedUnit)}
                  sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
                >
                  Edit Unit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setItemToDelete(selectedUnit);
                    setOpenDeleteDialog(true);
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
                >
                  Delete Unit
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      );
    }
  };

  const renderFormDialog = (isEdit = false) => (
    <Dialog
      open={isEdit ? openEditDialog : openAddDialog}
      onClose={() => {
        resetForm();
        if (isEdit) setOpenEditDialog(false);
        else setOpenAddDialog(false);
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
    >
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {isEdit ? 'Edit Unit' : 'Add Units'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {formErrors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {formErrors.general}
          </Alert>
        )}
        <form onSubmit={isEdit ? handleEditUnit : handleAddUnit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {!isEdit && (
              <TextField
                fullWidth
                label="Number of Units"
                name="numUnits"
                type="number"
                value={formData.numUnits}
                onChange={handleNumUnitsChange}
                required
                inputProps={{ min: 1, max: 10 }}
                helperText="Select number of units to add (max 10)"
                sx={{ borderRadius: '8px' }}
              />
            )}
            {formData.unitsData.map((unit, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                  Unit {index + 1}
                </Typography>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={unit.companyName}
                  disabled
                  helperText="Company name is set automatically"
                  sx={{ borderRadius: '8px' }}
                />
                <TextField
                  fullWidth
                  label="Unit Name"
                  name="unitName"
                  value={unit.unitName}
                  onChange={(e) => handleChange(e, index)}
                  required
                  error={!!formErrors[`unitName_${index}`]}
                  helperText={formErrors[`unitName_${index}`] || 'Enter unit name (e.g., Unit 1)'}
                  sx={{ borderRadius: '8px' }}
                />
                <FormControl fullWidth required error={!!formErrors[`unitType_${index}`]}>
                  <InputLabel>Unit Type</InputLabel>
                  <Select
                    name="unitType"
                    value={unit.unitType}
                    onChange={(e) => handleChange(e, index)}
                    label="Unit Type"
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="restaurant">Restaurant</MenuItem>
                    <MenuItem value="foodcourt">Food Court</MenuItem>
                    <MenuItem value="hotel">Hotel</MenuItem>
                  </Select>
                  {formErrors[`unitType_${index}`] && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {formErrors[`unitType_${index}`]}
                    </Typography>
                  )}
                </FormControl>
                <FormControl fullWidth required error={!!formErrors[`isPureVeg_${index}`]}>
                  <InputLabel>Pure Veg or Non-Veg</InputLabel>
                  <Select
                    name="isPureVeg"
                    value={unit.isPureVeg}
                    onChange={(e) => handleChange(e, index)}
                    label="Pure Veg or Non-Veg"
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="Pure Veg">Pure Veg</MenuItem>
                    <MenuItem value="Non-Veg">Non-Veg</MenuItem>
                  </Select>
                  {formErrors[`isPureVeg_${index}`] && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {formErrors[`isPureVeg_${index}`]}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={unit.city}
                  onChange={(e) => handleChange(e, index)}
                  required
                  error={!!formErrors[`city_${index}`]}
                  helperText={formErrors[`city_${index}`] || 'Enter city (e.g., Mumbai)'}
                  sx={{ borderRadius: '8px' }}
                />
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={unit.address}
                  onChange={(e) => handleChange(e, index)}
                  required
                  multiline
                  rows={3}
                  error={!!formErrors[`address_${index}`]}
                  helperText={formErrors[`address_${index}`] || ''}
                  sx={{ borderRadius: '8px' }}
                />
                <TextField
                  fullWidth
                  label="GSTIN"
                  name="gstin"
                  value={unit.gstin}
                  onChange={(e) => handleChange(e, index)}
                  //required
                  inputProps={{
                    maxLength: 15 // GSTIN is 15 chars
                  }}
                  error={!!formErrors[`gstin_${index}`]}
                  helperText={
                    formErrors[`gstin_${index}`] ||
                    'Format: 22AAAAA0000A1Z5'
                  }
                />
                <TextField
                  fullWidth
                  label="PAN"
                  name="pan"
                  value={unit.pan}
                  onChange={(e) => handleChange(e, index)}
                  //required
                  inputProps={{
                    maxLength: 10 // PAN is 10 chars
                  }}
                  error={!!formErrors[`pan_${index}`]}
                  helperText={
                    formErrors[`pan_${index}`] ||
                    'Format: ABCDE1234F'
                  }
                />
                <Box sx={{ height: 300, mt: 2 }}>
                  <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <LocationMarker setLocation={(location) => setLocation(index, location)} index={index} />
                  </MapContainer>
                </Box>
                <TextField
                  fullWidth
                  label="Location"
                  value={`${unit.location.lat.toFixed(4)}, ${unit.location.lng.toFixed(4)}`}
                  disabled
                  helperText="Click on the map to set location"
                />
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  resetForm();
                  if (isEdit) setOpenEditDialog(false);
                  else setOpenAddDialog(false);
                }}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
              >
                {isEdit ? 'Update Unit' : 'Add Units'}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
          Unit Management
        </Typography>
        {renderContent()}
        {renderFormDialog(false)}
        {renderFormDialog(true)}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Confirm Delete
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{itemToDelete?.unitName}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleDeleteUnit}
              loading={loading}
              color="error"
              variant="contained"
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <Alerts
          snackbar={snackbar}
          handleClose={handleCloseSnackbar} // Ensure handleClose matches Alerts component prop
          darkMode={darkMode}
        />
    <Dialog
  open={openUpiDialog}
  onClose={() => setOpenUpiDialog(false)}
  maxWidth="md"
  fullWidth
  PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
>
  <DialogTitle
    sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}
  >
    <Typography variant="h6">
      UPI Details for {selectedUnitForUpi?.unitName}
    </Typography>

     <Button
        variant="outlined"
        size="small"
        onClick={() => {
          setUpiForm({
            description: '',
            payeeName: '',
            upiAddress: ''
          });
        }}
      >
        Add New UPI
      </Button>
  </DialogTitle>
  <DialogContent>

{upiData.length > 0 &&
  upiData.some(upi => upi.upiAddress) && (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Description</strong></TableCell>
            <TableCell><strong>Payee Name</strong></TableCell>
            <TableCell><strong>UPI Address</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {upiData.map((upi, index) => (
            <TableRow key={index}>
              <TableCell>{upi.description}</TableCell>
              <TableCell>{upi.payeeName}</TableCell>
              <TableCell>{upi.upiAddress}</TableCell>

              <TableCell>
                <Button
                  size="small"
                  onClick={() => setUpiForm(upi)}
                >
                  Edit
                </Button>

                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    setUpiData(prev => prev.filter((_, i) => i !== index))
                  }
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
)}

  {/* 🔹 FORM ALWAYS ON TOP */}
  <Box
  sx={{
    display: 'flex',
    gap: 3,
    mb: 3,
    mt: 4,
    alignItems: 'stretch' // ✅ IMPORTANT
  }}
>
    
    {/* Left: form */}
    <Box
  sx={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
    mt:3
    //justifyContent: 'space-between' // ✅ pushes button to bottom
  }}
>
       <TextField
        label="Merchant/Payee Name"
        value={upiForm.payeeName}
        onChange={(e) =>
          setUpiForm({ ...upiForm, payeeName: e.target.value })
        }
      />
      <TextField
        label="UPI Address"
        value={upiForm.upiAddress}
        onChange={(e) =>
          setUpiForm({ ...upiForm, upiAddress: e.target.value })
        }
      />
      <TextField
        label="Description (Notes)"
        value={upiForm.description}
        onChange={(e) =>
          setUpiForm({ ...upiForm, description: e.target.value })
        }
      />


      {/* Download Button */}
<Button
  variant="contained" // ✅ filled button
  startIcon={<DownloadIcon />}
  sx={{
    mt: 2,
    backgroundColor: 'grey.700', // ✅ grey background
    color: '#fff',               // ✅ white text
    '&:hover': {
      backgroundColor: 'grey.800' // ✅ darker on hover
    }
  }}
  onClick={async () => {
    if (!qrRef.current) return;

    const canvas = await html2canvas(qrRef.current, {
      backgroundColor: null,
      scale: 2,// 🔥 better quality
       useCORS: true
    });

    const link = document.createElement('a');
    link.download = `${upiForm.payeeName || 'upi_qr'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }}
>
  Download QR
</Button>
    </Box>
<Box
 ref={qrRef}
      sx={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    p: 3,
    borderRadius: '16px',
    boxShadow: 3,
    bgcolor: '#fff',
    border: '1px solid #e0e0e0'
  }}
>
  {/* 🔹 Title */}
  <Typography
    variant="h6"
    sx={{ fontWeight: 'bold', letterSpacing: 1 }}
  >
    {upiForm.payeeName || "Payee Name"}
  </Typography>

  {/* 🔹 QR Code */}
  <Box
    sx={{
      my: 2,
      p: 1,
      backgroundColor: '#fff',
      display: 'inline-block'
    }}
  >
    <QRCodeCanvas
      value={`upi://pay?pa=${upiForm.upiAddress || ''}&pn=${upiForm.payeeName || ''}&tn=${upiForm.description || ''}`}
      size={180}
    />
  </Box>

  {/* 🔹 UPI ID */}
  <Typography
    variant="body2"
    sx={{ fontWeight: 500, color: 'gray' }}
  >
    {upiForm.upiAddress || "upi@bank"}
  </Typography>

  {/* 🔹 Scan Text */}
  <Typography
    variant="body2"
    sx={{ mt: 2, fontWeight: 500, mb:-1}}
  >
    Scan and pay with any BHIM UPI app
  </Typography>

  {/* 🔹 BHIM + UPI Logos */}
  {/* BHIM + UPI */}
<Box
  sx={{
    mt: 1,
    mb: -3,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1,
    lineHeight: 0 // ✅ removes hidden vertical gap
  }}
>
    <img
      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTRc6KcRLe7o9DZUJauB8YU6ZVHMoOWZ70XUw&s"
      style={{ width: 120 }}
    />
    <img
      src="https://cdn.iconscout.com/icon/free/png-256/free-upi-logo-icon-svg-download-png-1747946.png"
      style={{ width: 120 }}
    />
  </Box>

  {/* 🔹 Payment Apps */}
 <Box
  sx={{
    mt: -0.5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1,
    lineHeight: 0 // ✅ IMPORTANT
  }}
>
    <img
      src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Google_Pay_Logo.svg/3840px-Google_Pay_Logo.svg.png"
      style={{ width: 70}}
    />
    <img
      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSA53HqACzOxVqFCYmM_5SRXT4MeRX_e-NTRQ&s"
      style={{ width: 70 }}
    />
    <img
      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ-6fzljDBKlhouyBpr7OMZniH_WWGiMRFuxg&s"
      style={{ width: 70}}
    />
    <img
      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSwxyPdZBNfx-ulubSDlBHlSpVjl6x3Rs2Lzw&s"
      style={{ width: 70}}
    />
  </Box>
</Box>
  </Box>

  {/* 🔹 TABLE BELOW FORM */}
  

</DialogContent>

  <DialogActions>
    <Button onClick={() => setOpenUpiDialog(false)}>Cancel</Button>
   <LoadingButton
 onClick={async () => {
  if (!selectedUnitForUpi?.id) return;

  try {
    let updatedList = [...upiData];

    // 👉 Only validate if user is trying to ADD/EDIT
    if (upiForm.upiAddress) {
      const index = updatedList.findIndex(
        (item) => item.upiAddress === upiForm.upiAddress
      );

      if (index !== -1) {
        updatedList[index] = upiForm;
      } else {
        updatedList.push(upiForm);
      }
    }

    // 👉 Save even if list is empty (after delete)
    await setDoc(
      doc(db, 'units', selectedUnitForUpi.id),
      { upiDetails: updatedList },
      { merge: true }
    );

    setUpiData(updatedList);

    setUpiForm({
      description: '',
      payeeName: '',
      upiAddress: ''
    });

    setSnackbar({
      open: true,
      message: 'UPI saved successfully!',
      severity: 'success'
    });

    setOpenUpiDialog(false);

  } catch (err) {
    setSnackbar({ open: true, message: err.message, severity: 'error' });
  }
}}
  variant="contained"
>
  Save
</LoadingButton>
  </DialogActions>
</Dialog>
      </Box>
    </Box>
  );
};

export default AddUnit;